import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { HdFearCardComponent } from './hd-fear-card.component';

interface FearSectionVm {
  readonly paragraphs: readonly string[];
  readonly triggers: readonly string[];
  readonly strategies: readonly string[];
  readonly triggersLabel: string;
  readonly strategiesLabel: string;
}

/**
 * Eén volledige angst-sectie voor een poort: het cheat-sheet-blokje als titel
 * ({@link HdFearCardComponent}) met daaronder de uitgebreide rapport-info uit
 * de `human-design/fears`-scope (paragrafen, triggers en strategieën om de
 * angst te verlichten). Zelfstandig inzetbaar: het component leest alle content
 * zelf (taalwissel + NL-fallback) en kan dus zowel in het per-center
 * angsten-paneel als geïsoleerd getoond worden.
 */
@Component({
  selector: 'hd-fear-section',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdFearCardComponent],
  host: { '[attr.data-fear-gate]': 'gate()' },
  template: `
    <hd-fear-card [gate]="gate()" [size]="cardSize()" />

    <div class="hd-fear-section__content">
      @for (paragraph of vm().paragraphs; track $index) {
        <p class="hd-fear-section__text">{{ paragraph }}</p>
      }

      @if (vm().triggers.length > 0) {
        <!-- Elke trigger in een eigen cel, zoals de tabellen in het rapport. -->
        <table class="hd-fear-section__table hd-fear-section__table--triggers">
          <thead>
            <tr><th class="hd-fear-section__table-head" scope="col">{{ vm().triggersLabel }}</th></tr>
          </thead>
          <tbody>
            @for (trigger of vm().triggers; track $index) {
              <tr><td class="hd-fear-section__cell">{{ trigger }}</td></tr>
            }
          </tbody>
        </table>
      }

      @if (vm().strategies.length > 0) {
        <table class="hd-fear-section__table hd-fear-section__table--strategies">
          <thead>
            <tr><th class="hd-fear-section__table-head" scope="col">{{ vm().strategiesLabel }}</th></tr>
          </thead>
          <tbody>
            @for (strategy of vm().strategies; track $index) {
              <tr><td class="hd-fear-section__cell">{{ strategy }}</td></tr>
            }
          </tbody>
        </table>
      }
    </div>
  `,
  styles: `
    :host {
      display: block;
      color: #3d414e;
    }

    /* Ingesprongen onder het blokje (zoals de voice-secties): de paragrafen
       en tabellen lezen zo als detail bij de titel, niet als gelijke niveaus. */
    .hd-fear-section__content {
      padding: 10px 2px 0 16px;
    }

    /* Leestekst op 1em: gelijk aan de andere info-panelen (types, gate-info). */
    .hd-fear-section__text {
      margin: 0 0 9px;
      font-size: 1em;
      line-height: 1.55;
    }

    /* Triggers/strategieën als rapport-tabel: elke zin in een eigen cel. */
    .hd-fear-section__table {
      width: 100%;
      margin: 12px 0 0;
      border-collapse: separate;
      border-spacing: 0 6px;
    }

    .hd-fear-section__table-head {
      padding: 0 2px 1px;
      text-align: left;
      font-size: 0.95em;
      font-weight: 800;
    }

    .hd-fear-section__table--triggers .hd-fear-section__table-head {
      color: #a8524a;
    }

    .hd-fear-section__table--strategies .hd-fear-section__table-head {
      color: #2e7d54;
    }

    .hd-fear-section__cell {
      padding: 8px 12px;
      font-size: 0.95em;
      line-height: 1.5;
      background: #f7f7f7;
      border: 1px solid #e6e6e6;
      border-radius: 6px;
    }

    .hd-fear-section__table--triggers .hd-fear-section__cell {
      border-left: 4px solid rgba(168, 82, 74, 0.55);
    }

    .hd-fear-section__table--strategies .hd-fear-section__cell {
      border-left: 4px solid rgba(46, 125, 84, 0.55);
    }
  `,
})
export class HdFearSectionComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly gate = input.required<number>();
  /** Doorgegeven aan het blokje; `compact` voor smalle gedockte panelen. */
  readonly cardSize = input<'default' | 'compact'>('default');

  protected readonly vm = computed<FearSectionVm>(() => {
    const gate = this.gate();
    return {
      paragraphs: this.list(`gates.${gate}.paragraphs`),
      triggers: this.list(`gates.${gate}.triggers`),
      strategies: this.list(`gates.${gate}.strategies`),
      triggersLabel: this.text('labels.triggers'),
      strategiesLabel: this.text('labels.strategies'),
    };
  });

  constructor() {
    // Beide content-talen eager laden zodat taalwissel én NL-fallback meteen
    // correcte tekst opleveren, ook bij geïsoleerd (standalone) gebruik.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  private text(key: string): string {
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.cheatSheetTextIn('fears', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('fears', key, 'nl');
  }

  private list(key: string): readonly string[] {
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.cheatSheetListIn('fears', key, lang);
    if (value.length > 0 || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetListIn('fears', key, 'nl');
  }
}
